import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import Seo from "../components/Seo";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

const faqSections = [
  {
    id: "indigo-discount",
    title: "Indigo Discount Code Questions",
    questions: [
      {
        q: "Where do I add Indigo's discount code in the form?",
        a: "Indigo Research's discount code (or any other discount code) can only be added under the Affiliation code section of the form. If you do not have access to any code, please leave the affiliation code section blank.",
      },
      {
        q: "How long is Indigo Research's full fee waiver code active?",
        a: "Please note Indigo Research's code is only valid during each cycle's registration period. There will be no extensions.",
      },
      {
        q: "Where do I find Indigo Research's discount code?",
        a: "It is available on the IRO website's homepage. The code is Indigo100.",
      },
    ],
  },
  {
    id: "iro-form",
    title: "IRO Form Questions",
    questions: [
      {
        q: "Where do I find the IRO 2026 registration form?",
        a: "The form is available on the homepage of the IRO website. Here is a link for the form: https://www.internationalresearcholympiad.org/registration-and-eligibility.",
      },
      {
        q: "The IRO registration form is unavailable, not loading or lagging for me. What do I do?",
        a: "Please use a VPN to access the form. Some students from certain locations are experiencing this issue but using a VPN has resolved it.",
      },
      {
        q: "What do I add under the affiliation code section of the form?",
        a: "This section serves multiple purposes. If you have been referred by a student ambassador, please add their full name in this section of the form, or their referral code, to access a 25% discount on registration fees. Alternatively, if you have not been referred by a student and wish to use a discount code, you can add it here. However, if you have access to none of the above, then you can leave this section empty and continue filling out the rest of the form.",
      },
    ],
  },
  {
    id: "financial-aid",
    title: "Financial Aid Questions",
    questions: [
      {
        q: "I need financial aid as I can not afford the registration fees. How do I prove this?",
        a: "In order to prove your financial need, please upload documents that show that you have previously qualified for financial fee waivers for example, free meal plans, SAT fee waivers, etc. If you do not have access to such documents, you are welcome to submit your income statements. Please note, these income statements must show that you fall in a low income household in your country. Please note that if you submit documents that are unreadable or do not reflect on your financial circumstances at all, we will disqualify your application immediately.",
      },
      {
        q: "I have applied for financial aid, but have not heard back on the outcome of my application. When can I expect to hear back?",
        a: "Due to a high volume of applications, we require at least 2 weeks to process your financial aid documents and may even contact you with further questions. Therefore, financial aid forms must be submitted by the deadline published alongside registration each cycle.",
      },
      {
        q: "Will applying for financial aid hurt my chances of success?",
        a: "No, this will have no bearing on your success.",
      },
    ],
  },
  {
    id: "exam-format",
    title: "Exam Format",
    questions: [
      {
        q: "How will the exam be conducted?",
        a: "The Opens and Semi-Finals rounds are both conducted online. You will receive a unique login link and password via email at the email address you used to register for the IRO. The exam is held at two timeslots: 8 am EDT and 8 pm EDT.",
      },
      {
        q: "Is the exam open for 24 hours?",
        a: "No, the exam is only open for 1 hour at the two global times mentioned in the sign up form. You can sign up for either 8 am EDT or 8 pm EDT. You will have a 15 minute window to access the exam, that is, you can log in up to 15 mins after the exam starting time you have chosen in the sign up form, beyond which no logins will be entertained. Once you begin your exam within the 15 minute window, it will automatically submit after an hour.",
      },
      {
        q: "I have not received the sign up form email. What do I do?",
        a: "Please check all your email folders including spam and promotions as emails can get redirected there and not show up in your main mailbox. Once you have checked, and you are still unable to locate the email, please email us at contact@internationalresearcholympiad.org sending the following details (please make sure they are the same as the details you entered when you signed up): 1. Full Name 2. Email address 3. School Name",
      },
      {
        q: "I have signed up twice. Will this affect my participation?",
        a: "Yes, more than one sign ups by the same student or using the same email to sign up multiple students will result in disqualification.",
      },
      {
        q: "When will I receive my exam login details?",
        a: "The exam login details will be shared via email between the 12th and 13th of March.",
      },
      {
        q: "What if my internet connection fails me?",
        a: "While we understand that internet disruptions can happen, we can not be responsible for the issues they may cause. So we recommend that you choose a place that offers a steady internet connection, such as your school or a library.",
      },
      {
        q: "Will our videos be on during the exam?",
        a: "No, we will not video record you during the exam, but the exam has several anti-cheat mechanisms built in, which will help us identify if students have cheated. Students who cheat will be disqualified.",
      },
    ],
  },
  {
    id: "platform-related",
    title: "Platform Related Questions",
    questions: [
      {
        q: "Do I need to download the Synap app to access the exam?",
        a: "No, you don't need to do that. Simply click the link shared via email. If the link does not work, then copy paste the link provided in the email on your browser to access the testing platform.",
      },
      {
        q: "What details will I need to access the exam?",
        a: "We will share a unique passcode which you will use together with your email to access your exam.",
      },
      {
        q: "Is the exam for both timezones the same?",
        a: "No, they are different but the difficulty level is the same.",
      },
      {
        q: "How do I prepare for the Opens Round?",
        a: "Simply practise the past exams and mock exams shared with you earlier. No extra preparation is required.",
      },
      {
        q: "How is the exam formatted?",
        a: "There will be 32 questions in total. The exam will include 4 research papers and there will be 8 questions per research paper. It will be an MCQ based exam only and 60 minutes long.",
      },
      {
        q: "Can I get extra time to finish my exam?",
        a: "No. The exam is an hour long for every student.",
      },
      {
        q: "Do I need to keep my video or microphone on?",
        a: "At the Opens stage, you do not need to keep your video or your microphone on. We have installed anti-cheat features within the platform which will prevent cheating.",
      },
      {
        q: "What happens if someone cheats?",
        a: "They will be disqualified and barred from entering the competition in the future.",
      },
    ],
  },
  {
    id: "semi-finals",
    title: "Semi-Finals Queries",
    questions: [
      {
        q: "How do I access the exam?",
        a: null,
        richAnswer: (
          <>
            <p style={{ margin: "0 0 12px 0" }}>
              To access the exam, certain exam set up conditions have to be met. You can find them below:
            </p>
            <ul style={{ margin: 0, paddingLeft: "20px", lineHeight: "1.8" }}>
              <li>Use Google Chrome only (no other browser is supported)</li>
              <li>
                Install the Synoptic Chrome Plugin (here is the link:{" "}
                <a href="https://chromewebstore.google.com/detail/synoptic/ablllcpeboaekooacnnpbiahlcekijon" target="_blank" rel="noopener noreferrer" style={{ color: "#004d99", fontWeight: "600", wordBreak: "break-all" }}>
                  https://chromewebstore.google.com/detail/synoptic/ablllcpeboaekooacnnpbiahlcekijon
                </a>)
              </li>
              <li>Use a Laptop or Desktop computer ONLY (no other device is supported)</li>
            </ul>
          </>
        ),
      },
      {
        q: "I am unable to access the exam.",
        a: "If you are unable to access the exam then you have not downloaded and installed the Synoptic Chrome extension in advance. If this is not done, you will be unable to start the exam.",
      },
      {
        q: "Can I do a test-run of the exam to ensure everything is working?",
        a: "Yes, all semi-finalists can do a test-run at anytime before the exam. We recommend you do this test-run so if you face any issues you can let us know in advance and we can help troubleshoot for you. Students who complain during the exam of any disruptions will not be catered to as we are providing all students with a test-run in advance.",
      },
      {
        q: "What else do I need to know before I start the exam?",
        a: null,
        richAnswer: (
          <>
            <p style={{ margin: "0 0 12px 0" }}>
              Before you can begin, the system will check all the requirements are met. You must:
            </p>
            <ul style={{ margin: "0 0 12px 0", paddingLeft: "20px", lineHeight: "1.8" }}>
              <li>Have Synoptic plugin installed and active</li>
              <li>Use Chrome in full-screen mode</li>
              <li>Close all additional tabs</li>
              <li>Disconnect any external monitors</li>
              <li>Grant screen-sharing permissions when prompted</li>
            </ul>
            <p style={{ margin: 0 }}>
              If any of the above conditions are not met, you will NOT be allowed to start the exam.
            </p>
          </>
        ),
      },
      {
        q: "What do I need to be careful of while I'm attempting the exam so I am not flagged for cheating?",
        a: null,
        richAnswer: (
          <>
            <p style={{ margin: "0 0 12px 0" }}>
              The system will be monitoring your session. Please note:
            </p>
            <ul style={{ margin: "0 0 12px 0", paddingLeft: "20px", lineHeight: "1.8" }}>
              <li>Leaving full screen, opening new tabs, or breaking exam rules may be flagged.</li>
              <li>Violations can affect your exam attempt.</li>
              <li>Your audio and video settings must grant permission to the platform as it will be recording you.</li>
            </ul>
          </>
        ),
      },
      {
        q: "I am unclear about how to attempt the Semi-Finals exam. Can you give details?",
        a: null,
        richAnswer: (
          <>
            <p style={{ margin: 0 }}>
              Yes, all the information you require regarding the Semis is available in the Semi-Finals curriculum guide. It has been attached here for your ease:{" "}
              <a href="https://docs.google.com/document/d/1wVRxCWApHyJ3NH1YUa5WV5_dirOwp9Yoi8qdnb-u7Ak/edit?usp=drivesdk" target="_blank" rel="noopener noreferrer" style={{ color: "#004d99", fontWeight: "600", wordBreak: "break-all" }}>
                Semi-Finals Curriculum Guide
              </a>
              . Please review this thoroughly as it will answer all your exam related queries.
            </p>
          </>
        ),
      },
    ],
  },
  {
    id: "certificates",
    title: "Certificates & Results",
    questions: [
      {
        q: "How do I get my certificate?",
        a: "Certificates for the 2026 IRO are now available. Visit the Certificate page (in the top menu) and enter the email you registered with to view and download your certificate.",
      },
    ],
  },
  {
    id: "student-ambassador",
    title: "Student Ambassador Program",
    questions: [
      {
        q: "I have received an email to join the Discord group, but it is either inactive, or I have not received further communication. What do I do?",
        a: "Please note that we are NOT using Discord. Please sign up for the Student Ambassador Program using the form on our website. Only students who have registered for IRO 2026 are eligible to apply as Student Ambassadors. Once you apply for the role of a Student Ambassador, we will process your application and if we find you to be a suitable fit, you will receive further communication from us. Unfortunately, due to a high volume of Student Ambassador applications, we are unable to respond individually to each unsuccessful applicant. Instead, only successful candidates will be contacted.",
      },
      {
        q: "I have successfully helped 10 students register. How do I unlock my 100 USD research funding?",
        a: "Congratulations! Please email us with the materials or resources you require, and let us know how you will use the 100 USD in funding. We will require detailed information, and if your request is viewed as legitimate, in that it is genuinely helping your community build their research interest, we will help you buy the requisite materials. Please note that this funding is not meant for any other purpose but to further research in your school community.",
      },
      {
        q: "Does the above mean that the 100 USD will not get credited to the student's bank account or to that of a teacher's?",
        a: "No. However, under exceptional circumstances, it may be credited to a teacher's account, but this will be determined by the IRO team.",
      },
      {
        q: "What do I gain by signing up as a Student Ambassador?",
        a: "Please view the Student Ambassador section of the website for more information.",
      },
    ],
  },
];

// Helper function to render answer text with links
const renderAnswerWithLinks = (text) => {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = text.split(urlRegex);

  return parts.map((part, index) => {
    if (part.match(urlRegex)) {
      // Remove trailing period if present
      const cleanUrl = part.replace(/\.$/, "");
      const hasPeriod = part.endsWith(".");
      return (
        <span key={index}>
          <a
            href={cleanUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: "#004d99",
              fontWeight: "600",
              wordBreak: "break-all",
            }}
          >
            {cleanUrl}
          </a>
          {hasPeriod && "."}
        </span>
      );
    }
    return part;
  });
};

const FAQItem = ({ question, answer, richAnswer, isOpen, onClick }) => (
  <div
    style={{
      background: "#fff",
      borderRadius: "12px",
      marginBottom: "12px",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
      border: "1px solid #e8eef3",
      overflow: "hidden",
    }}
  >
    <button
      onClick={onClick}
      style={{
        width: "100%",
        padding: "16px 20px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        background: "none",
        border: "none",
        cursor: "pointer",
        textAlign: "left",
      }}
    >
      <span
        style={{
          fontSize: "0.95rem",
          fontWeight: "600",
          color: "#1a1a2e",
          paddingRight: "12px",
          lineHeight: "1.4",
        }}
      >
        {question}
      </span>
      {isOpen ? (
        <ExpandLessIcon style={{ color: "#004d99", flexShrink: 0 }} />
      ) : (
        <ExpandMoreIcon style={{ color: "#004d99", flexShrink: 0 }} />
      )}
    </button>
    {isOpen && (
      <div
        style={{
          padding: "0 20px 16px 20px",
          borderTop: "1px solid #e8eef3",
        }}
      >
        {richAnswer ? (
          <div
            style={{
              fontSize: "0.9rem",
              color: "#555",
              lineHeight: "1.8",
              marginTop: "16px",
              wordBreak: "break-word",
            }}
          >
            {richAnswer}
          </div>
        ) : (
          <p
            style={{
              fontSize: "0.9rem",
              color: "#555",
              lineHeight: "1.8",
              margin: "16px 0 0 0",
              wordBreak: "break-word",
            }}
          >
            {renderAnswerWithLinks(answer)}
          </p>
        )}
      </div>
    )}
  </div>
);

// Mobile section selector component
const MobileSectionSelector = ({ sections, activeSection, onSelect }) => (
  <div
    style={{
      display: "flex",
      overflowX: "auto",
      gap: "8px",
      paddingBottom: "16px",
      marginBottom: "24px",
      WebkitOverflowScrolling: "touch",
    }}
  >
    {sections.map((section) => (
      <button
        key={section.id}
        onClick={() => onSelect(section.id)}
        style={{
          flexShrink: 0,
          padding: "8px 16px",
          borderRadius: "20px",
          border:
            activeSection === section.id
              ? "2px solid #004d99"
              : "1px solid #e8eef3",
          background:
            activeSection === section.id ? "rgba(0, 77, 153, 0.08)" : "#fff",
          color: activeSection === section.id ? "#004d99" : "#555",
          fontSize: "0.85rem",
          fontWeight: activeSection === section.id ? "600" : "500",
          cursor: "pointer",
          whiteSpace: "nowrap",
        }}
      >
        {section.title}
      </button>
    ))}
  </div>
);

function FAQ() {
  const [openItems, setOpenItems] = useState({});
  const [activeSection, setActiveSection] = useState("indigo-discount");
  const [isMobile, setIsMobile] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 900);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    if (location.hash) {
      const sectionId = location.hash.slice(1);
      if (faqSections.find((s) => s.id === sectionId)) {
        setActiveSection(sectionId);
      }
    }
  }, [location]);

  useEffect(() => {
    if (isMobile) return;

    const handleScroll = () => {
      const sectionElements = faqSections
        .map((s) => ({
          id: s.id,
          element: document.getElementById(s.id),
        }))
        .filter((s) => s.element);

      for (const section of sectionElements) {
        const rect = section.element.getBoundingClientRect();
        if (rect.top <= 150 && rect.bottom > 150) {
          setActiveSection(section.id);
          break;
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isMobile]);

  const scrollToSection = (id) => {
    setActiveSection(id);
    const element = document.getElementById(id);
    if (element) {
      const offset = isMobile ? 20 : 100;
      const elementPosition =
        element.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({ top: elementPosition - offset, behavior: "smooth" });
    }
  };

  const toggleItem = (sectionId, questionIndex) => {
    const key = `${sectionId}-${questionIndex}`;
    setOpenItems((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  return (
    <div>
      <Seo
        title="FAQ"
        path="/faq"
        description="Frequently asked questions about the International Research Olympiad — eligibility, rounds, scoring, fees, fee waivers, and how to take part."
      />
      {/* Hero Section */}
      <div
        style={{
          backgroundColor: "#f8f9fa",
          padding: isMobile ? "40px 0 30px 0" : "60px 0 50px 0",
        }}
      >
        <div className="container">
          <div style={{ maxWidth: "800px" }}>
            <h1
              style={{
                fontSize: isMobile ? "1.75rem" : "clamp(2rem, 4vw, 2.5rem)",
                fontWeight: "700",
                color: "#1a1a2e",
                marginBottom: "12px",
                letterSpacing: "-0.02em",
              }}
            >
              Frequently Asked Questions
            </h1>
            <p
              style={{
                fontSize: isMobile ? "1rem" : "1.1rem",
                color: "#555",
                lineHeight: "1.7",
              }}
            >
              Find answers to common questions about the International Research
              Olympiad.
            </p>
          </div>
        </div>
      </div>

      <div
        className="container"
        style={{ padding: isMobile ? "30px 0 60px 0" : "50px 0 80px 0" }}
      >
        {/* Mobile Section Selector */}
        {isMobile && (
          <MobileSectionSelector
            sections={faqSections}
            activeSection={activeSection}
            onSelect={scrollToSection}
          />
        )}

        <div style={{ display: "flex", gap: "50px", flexWrap: "wrap" }}>
          {/* Sidebar Navigation - Desktop Only */}
          {!isMobile && (
            <div
              style={{
                width: "220px",
                flexShrink: 0,
                position: "sticky",
                top: "100px",
                alignSelf: "flex-start",
                height: "fit-content",
              }}
            >
              <nav
                style={{ display: "flex", flexDirection: "column", gap: "4px" }}
              >
                {faqSections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => scrollToSection(section.id)}
                    style={{
                      background:
                        activeSection === section.id
                          ? "rgba(0, 77, 153, 0.08)"
                          : "transparent",
                      border: "none",
                      borderLeft:
                        activeSection === section.id
                          ? "3px solid #004d99"
                          : "3px solid transparent",
                      padding: "12px 16px",
                      textAlign: "left",
                      cursor: "pointer",
                      fontSize: "0.9rem",
                      fontWeight: activeSection === section.id ? "600" : "500",
                      color: activeSection === section.id ? "#004d99" : "#555",
                      borderRadius: "0 8px 8px 0",
                      transition: "all 0.2s ease",
                    }}
                  >
                    {section.title}
                  </button>
                ))}
              </nav>
            </div>
          )}

          {/* Content */}
          <div
            style={{
              flex: 1,
              minWidth: 0,
              maxWidth: isMobile ? "100%" : "800px",
            }}
          >
            {faqSections.map((section) => (
              <section
                key={section.id}
                id={section.id}
                style={{ marginBottom: isMobile ? "40px" : "50px" }}
              >
                <h2
                  style={{
                    fontSize: isMobile ? "1.25rem" : "1.5rem",
                    fontWeight: "600",
                    color: "#1a1a2e",
                    marginBottom: "20px",
                    paddingBottom: "12px",
                    borderBottom: "2px solid #e8eef3",
                  }}
                >
                  {section.title}
                </h2>

                {section.questions.map((item, idx) => (
                  <FAQItem
                    key={idx}
                    question={item.q}
                    answer={item.a}
                    richAnswer={item.richAnswer}
                    isOpen={openItems[`${section.id}-${idx}`] || false}
                    onClick={() => toggleItem(section.id, idx)}
                  />
                ))}
              </section>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default FAQ;
